import { createParser } from 'nuqs';
import {
  DateRangePreset,
  isCustomPreset,
  parsePresetToken,
  PRESET_MENU_ORDER,
  serializePresetToken,
} from './date-range-presets';

const VALID_PRESET_IDS = new Set<string>(PRESET_MENU_ORDER);

/**
 * URL-serializable value for the date range filter.
 * For non-custom presets only `preset` is needed; custom presets also include ISO `from`/`to`.
 */
export interface DateRangeFilterValue {
  preset: DateRangePreset;
  from?: string;
  to?: string;
}

function parseLegacyPresetId(rawPresetId: unknown): DateRangePreset | null {
  return parsePresetToken(String(rawPresetId));
}

/**
 * nuqs parser for date range filter.
 * Compatibility behavior:
 * - Built-ins remain plain strings in URL.
 * - Dynamic presets use `presetToken` payload.
 * - Legacy custom JSON `{ presetId, from, to }` is still accepted.
 */
export const parseAsDateRangeFilter = createParser<DateRangeFilterValue | null>({
  parse(value) {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (VALID_PRESET_IDS.has(trimmed)) {
      const legacyPreset = parsePresetToken(trimmed);
      if (!legacyPreset || isCustomPreset(legacyPreset)) {
        return null;
      }
      return { preset: legacyPreset };
    }

    const tokenPreset = parsePresetToken(trimmed);
    if (tokenPreset && !isCustomPreset(tokenPreset)) {
      return { preset: tokenPreset };
    }

    try {
      const raw = JSON.parse(trimmed) as unknown;
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const rawObj = raw as Record<string, unknown>;
      const from = typeof rawObj.from === 'string' ? rawObj.from : undefined;
      const to = typeof rawObj.to === 'string' ? rawObj.to : undefined;

      let preset: DateRangePreset | null = null;
      if (typeof rawObj.presetToken === 'string') {
        preset = parsePresetToken(rawObj.presetToken);
      } else if ('presetId' in rawObj) {
        preset = parseLegacyPresetId(rawObj.presetId);
      }

      if (!preset) {
        return null;
      }

      if (isCustomPreset(preset) && (!from || !to)) {
        return null;
      }

      return { preset, from, to };
    } catch {
      return null;
    }
  },
  serialize(value): string {
    if (value == null) {
      return '';
    }

    if (typeof value.preset === 'string' && !isCustomPreset(value.preset)) {
      return value.preset;
    }

    return JSON.stringify({
      presetToken: serializePresetToken(value.preset),
      ...(value.from != null && { from: value.from }),
      ...(value.to != null && { to: value.to }),
    });
  },
  eq(a, b) {
    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return false;
    }

    return (
      serializePresetToken(a.preset) === serializePresetToken(b.preset) &&
      (a.from ?? '') === (b.from ?? '') &&
      (a.to ?? '') === (b.to ?? '')
    );
  },
});
